import { useMemo } from "react";
import { AccordionList, Icon } from "@elaniintc/goes-design-system";
import { useGetFaqs } from "@/queries/faq";
import type { FaqGroupedItem, FaqItemType } from "@/types/entities/faq";
import { Skeleton } from "@/components/ui/skeleton";

// eslint-disable-next-line react-refresh/only-export-components
export const groupByCategory = (data: FaqItemType[]): FaqGroupedItem[] => {
  const grouped = data.reduce<Record<string, FaqGroupedItem>>((acc, curr) => {
    const { id, category, question, response } = curr;

    if (!acc[category]) {
      acc[category] = {
        category,
        items: [],
      };
    }

    acc[category].items.push({
      value: id,
      title: question,
      content: response,
    });

    return acc;
  }, {});

  return Object.values(grouped);
};

export default function FaqPage() {
  const { data: dataFaqs, isLoading } = useGetFaqs();

  const fixedData: FaqGroupedItem[] | undefined = useMemo(() => {
    return dataFaqs?.data ? groupByCategory(dataFaqs.data) : [];
  }, [dataFaqs?.data]);

  return (
    <div className="h-full w-full space-y-16 px-2 md:px-10">
      {isLoading ? (
        <div data-testid="page-skeletor" className=" space-y-16">
          {[1, 2].map((item) => (
            <div key={item} className="grid grid-cols-10">
              <div className="col-span-10 xl:col-span-3 mb-4 flex justify-center xl:justify-start">
                <Skeleton className="h-8 w-[250px] mb-10" />
              </div>
              <div className="col-span-10 xl:col-span-7 space-y-2 max-w-3xl">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    className="border border-neutral-quiet p-6 rounded-lg flex justify-between"
                    key={item}
                  >
                    <Skeleton className="h-6 w-full" />
                    <Icon
                      type="fas"
                      name="angle-down"
                      className="text-heading-quiet text-lg w-4 h-4 pointer-events-none shrink-0 translate-y-0.5 transition-transform duration-200 ml-4"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        fixedData?.map((item) => (
          <div
            data-testid="page-accordion"
            key={item.category}
            className="grid grid-cols-10"
          >
            <div className="col-span-10 xl:col-span-3 mb-4 text-center xl:text-left text-2xl font-semibold text-heading">
              <h4>{item.category}</h4>
            </div>
            <div className="col-span-10 xl:col-span-7">
              <AccordionList
                type="multiple"
                collapsible
                className="max-w-3xl"
                items={item.items}
                icon={
                  <Icon
                    type="fas"
                    name="angle-down"
                    className="text-heading-quiet text-lg w-4 h-4 pointer-events-none shrink-0 translate-y-0.5 transition-transform duration-200"
                  />
                }
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
